import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Disclosure } from "@headlessui/react";
import { Alert } from "reactstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import SimpleTable from "../../../../components/SimpleTable";
import DateInput from "../../../../components/Inputs/DateInput";
import Button from "../../../../components/Button";
import { useCompanyDateFormat } from "../../../../utils/hooks/company";
import { ProfileContext } from "../../context";
import { useLedgerHistoryApi } from "./api";
import {
    buildLedgerCsv,
    buildLedgerRows,
    describeLedgerRow,
    downloadCsv,
    formatChange,
    formatHours,
    formatLedgerDate,
    ledgerCsvFilename,
    parseLedgerDate,
    toLedgerDate,
} from "./ledger";
import "./style.scss";

const columnsHelper = createColumnHelper();

const warningText = (warning) => {
    if (typeof warning === "string") return warning;
    return warning?.message || JSON.stringify(warning);
};

// asPanel: render inside the History accordion (Disclosure.Panel); false for a standalone container such as a drawer
const LedgerContent = ({ plan, toolbar, asPanel = true }) => {
    const { t } = useTranslation();
    const { isSelf, user } = useContext(ProfileContext);
    const companyDateFormat = useCompanyDateFormat();
    const { getLedger, ledger, loading } = useLedgerHistoryApi();

    // YYYY-MM-DD strings; undefined lets the backend use the plan start / today
    const [from, setFrom] = useState();
    const [to, setTo] = useState();

    const rangeError = from && to && from > to ? t("ledger-invalid-range") : null;

    useEffect(() => {
        if (!plan?.id || rangeError) return;
        const controller = new AbortController();
        getLedger({ isSelf, userId: user?.id, planId: plan.id, from, to }, controller);
        return () => controller.abort();
    }, [getLedger, isSelf, user?.id, plan?.id, from, to, rangeError]);

    const rows = useMemo(() => buildLedgerRows(ledger), [ledger]);

    const columns = useMemo(
        () => [
            columnsHelper.accessor("date", {
                header: t("date"),
                cell: (info) => formatLedgerDate(info.getValue(), companyDateFormat),
            }),
            columnsHelper.accessor("planCode", {
                header: t("plan"),
            }),
            columnsHelper.display({
                id: "description",
                header: t("description"),
                cell: ({ row }) => {
                    const text = describeLedgerRow(row.original, t, companyDateFormat);
                    // table cells do not wrap (argon); long descriptions wrap here instead of widening the page
                    return (
                        <div className="ledger-description">
                            {row.original.type === "opening" ? <b>{text}</b> : text}
                        </div>
                    );
                },
            }),
            columnsHelper.accessor("change", {
                header: <div className="text-right">{t("ledger-change-hours")}</div>,
                cell: (info) => {
                    const value = info.getValue();
                    if (value === null || value === undefined) return null;
                    const isPending = info.row.original.type === "pending";
                    const color = isPending ? "" : value > 0 ? "text-green" : value < 0 ? "text-danger" : "";
                    return <div className={`text-right ${color}`}>{formatChange(value)}</div>;
                },
            }),
            columnsHelper.accessor("balance", {
                header: <div className="text-right">{t("ledger-balance")}</div>,
                cell: (info) =>
                    info.row.original.type === "pending" ? null : (
                        <div className="text-right font-weight-bold">{formatHours(info.getValue())}</div>
                    ),
            }),
        ],
        [t, companyDateFormat]
    );

    const getRowProps = useCallback(
        (row) => (row.original.type === "pending" ? { className: "text-muted font-italic ledger-pending-row" } : {}),
        []
    );

    const onExport = useCallback(() => {
        if (!ledger) return;
        downloadCsv(buildLedgerCsv(ledger, t, companyDateFormat), ledgerCsvFilename(ledger));
    }, [ledger, t, companyDateFormat]);

    const summary = ledger?.summary;
    // statement layout: movements in the range on the left, the resulting balances on the right
    const summaryGroups = summary
        ? [
              [
                  { label: t("ledger-beginning"), value: summary.opening },
                  { label: t("ledger-accrued"), value: summary.accrued },
                  { label: t("ledger-used"), value: summary.used },
                  { label: t("ledger-adjustments"), value: summary.adjustments },
                  ...(Number(summary.forfeited) > 0 ? [{ label: t("ledger-forfeited"), value: summary.forfeited }] : []),
              ],
              [
                  { label: t("ledger-final-balance"), value: summary.final, strong: true },
                  { label: t("ledger-pending"), value: summary.pending },
                  // a transfer adjustment waiting for approval adds or takes hours: its own line, signed
                  ...(Number(summary.pendingAdjustments)
                      ? [{ label: t("ledger-pending-adjustments"), value: summary.pendingAdjustments, signed: true }]
                      : []),
                  { label: t("ledger-available-after-pending"), value: summary.availableAfterPending, strong: true },
              ],
          ]
        : [];

    const fromValue = parseLedgerDate(from || ledger?.from)?.toDate();
    const toValue = parseLedgerDate(to || ledger?.to)?.toDate();

    const content = (
        <>
            {toolbar}
            <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
                <div className="d-flex flex-wrap gap-3">
                    <div>
                        <small className="text-muted d-block">{t("from")}</small>
                        <DateInput
                            placeholderText={t("from")}
                            onChange={(value) => setFrom(toLedgerDate(value))}
                            value={fromValue}
                        />
                    </div>
                    <div className="d-flex flex-column">
                        <small className="text-muted d-block">{t("ledger-to")}</small>
                        <DateInput
                            placeholderText={t("ledger-to")}
                            onChange={(value) => setTo(toLedgerDate(value))}
                            value={toValue}
                        />
                    </div>
                    {(from || to) && (
                        <div className="d-flex align-items-end">
                            <Button
                                size="sm"
                                color="link"
                                onClick={() => {
                                    setFrom(undefined);
                                    setTo(undefined);
                                }}
                            >
                                {t("ledger-reset-dates")}
                            </Button>
                        </div>
                    )}
                </div>
                <Button
                    size="sm"
                    color="primary"
                    onClick={onExport}
                    disabled={!ledger || loading}
                    icon={<FontAwesomeIcon icon={faDownload} />}
                >
                    {t("ledger-export-csv")}
                </Button>
            </div>
            {rangeError && <small className="text-danger d-block mb-2">{rangeError}</small>}

            {Array.isArray(ledger?.warnings) && ledger.warnings.length > 0 && (
                <Alert color="warning" className="py-2 px-3 text-sm">
                    <ul className="mb-0 pl-3">
                        {ledger.warnings.map((warning, i) => (
                            <li key={i}>{warningText(warning)}</li>
                        ))}
                    </ul>
                </Alert>
            )}

            <SimpleTable
                data={ledger ? rows : []}
                columns={columns}
                loading={loading}
                footer={false}
                getRowProps={getRowProps}
                size="sm"
            />

            {summary && !loading && (
                <div className="ledger-summary border rounded mt-3">
                    {summaryGroups.map((items, index) => (
                        <dl key={index} className="ledger-summary-group">
                            {items.map(({ label, value, strong, signed }) => (
                                <div key={label} className={`ledger-summary-row${strong ? " ledger-summary-row--strong" : ""}`}>
                                    <dt className="text-muted">{label}</dt>
                                    <dd>{signed ? formatChange(value) : formatHours(value)}</dd>
                                </div>
                            ))}
                        </dl>
                    ))}
                </div>
            )}
        </>
    );

    return asPanel ? (
        <Disclosure.Panel key={plan.id} className="history-accordion-panel history-ledger-panel">
            {content}
        </Disclosure.Panel>
    ) : (
        <div className="history-ledger-standalone">{content}</div>
    );
};

export default LedgerContent;
